import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminPanel.css';
import { getAllUsers, createUser, updateUser, toggleUserStatus, deleteUser } from '../services/userService';
import { getAllRoles, createRole, updateRole, deleteRole } from '../services/roleService';
import { diagramFiles, diagramCategories } from '../data/diagrams';

function AdminPanel({ onLogout, currentUser, currentRole }) {
  const navigate = useNavigate();
  // Solo un Super Admin puede ver a otros Super Admins. Un Admin (isAdmin)
  // ve a los admins y al resto de usuarios, pero NUNCA a los super admins.
  const viewerIsSuperAdmin = !!currentRole?.isSuperAdmin;
  const [activeTab, setActiveTab] = useState('users'); // 'users' o 'roles'

  // Estado para usuarios
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingRole, setEditingRole] = useState(null);

  // Formulario de usuario
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    displayName: '',
    roleId: ''
  });

  // Formulario de rol
  const [roleForm, setRoleForm] = useState({
    name: '',
    modules: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, rolesData] = await Promise.all([
        getAllUsers(),
        getAllRoles()
      ]);
      setUsers(usersData);
      setRoles(rolesData);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // CRUD Usuarios
  const handleCreateUser = async () => {
    try {
      if (!userForm.email || !userForm.password || !userForm.displayName || !userForm.roleId) {
        alert('Todos los campos son obligatorios');
        return;
      }
      await createUser(userForm.email, userForm.password, userForm.displayName, userForm.roleId);
      alert('Usuario creado exitosamente');
      setShowUserModal(false);
      resetUserForm();
      loadData();
    } catch (error) {
      console.error('Error creando usuario:', error);
      alert('Error al crear usuario: ' + error.message);
    }
  };

  const handleUpdateUser = async () => {
    try {
      await updateUser(editingUser.uid, {
        displayName: userForm.displayName,
        roleId: userForm.roleId
      });
      alert('Usuario actualizado exitosamente');
      setShowUserModal(false);
      setEditingUser(null);
      resetUserForm();
      loadData();
    } catch (error) {
      console.error('Error actualizando usuario:', error);
      alert('Error al actualizar usuario');
    }
  };

  const handleToggleUserStatus = async (uid, currentStatus) => {
    if (window.confirm(`¿Seguro que deseas ${currentStatus ? 'desactivar' : 'activar'} este usuario?`)) {
      try {
        await toggleUserStatus(uid, !currentStatus);
        loadData();
      } catch (error) {
        console.error('Error cambiando estado:', error);
        alert('Error al cambiar estado del usuario');
      }
    }
  };

  const handleDeleteUser = async (uid) => {
    if (window.confirm('¿Seguro que deseas eliminar este usuario? Esta acción no se puede deshacer.')) {
      try {
        await deleteUser(uid);
        alert('Usuario eliminado exitosamente');
        loadData();
      } catch (error) {
        console.error('Error eliminando usuario:', error);
        alert('Error al eliminar usuario');
      }
    }
  };

  // CRUD Roles
  const handleCreateRole = async () => {
    try {
      if (!roleForm.name) {
        alert('El nombre del rol es obligatorio');
        return;
      }
      await createRole({
        name: roleForm.name,
        permissions: {
          modules: roleForm.modules
        },
        isSuperAdmin: false
      });
      alert('Rol creado exitosamente');
      setShowRoleModal(false);
      resetRoleForm();
      loadData();
    } catch (error) {
      console.error('Error creando rol:', error);
      alert('Error al crear rol');
    }
  };

  const handleUpdateRole = async () => {
    try {
      await updateRole(editingRole.id, {
        name: roleForm.name,
        permissions: {
          modules: roleForm.modules
        }
      });
      alert('Rol actualizado exitosamente');
      setShowRoleModal(false);
      setEditingRole(null);
      resetRoleForm();
      loadData();
    } catch (error) {
      console.error('Error actualizando rol:', error);
      alert('Error al actualizar rol');
    }
  };

  const handleDeleteRole = async (roleId) => {
    const usersWithRole = users.filter(u => u.roleId === roleId);
    if (usersWithRole.length > 0) {
      alert('No puedes eliminar un rol que tiene usuarios asignados');
      return;
    }

    if (window.confirm('¿Seguro que deseas eliminar este rol?')) {
      try {
        await deleteRole(roleId);
        alert('Rol eliminado exitosamente');
        loadData();
      } catch (error) {
        console.error('Error eliminando rol:', error);
        alert('Error al eliminar rol');
      }
    }
  };

  const openUserModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setUserForm({
        email: user.email,
        password: '',
        displayName: user.displayName,
        roleId: user.roleId
      });
    }
    setShowUserModal(true);
  };

  const openRoleModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({
        name: role.name,
        modules: role.permissions.modules || []
      });
    }
    setShowRoleModal(true);
  };

  const resetUserForm = () => {
    setUserForm({ email: '', password: '', displayName: '', roleId: '' });
    setEditingUser(null);
  };

  const resetRoleForm = () => {
    setRoleForm({ name: '', modules: [] });
    setEditingRole(null);
  };

  const toggleModule = (moduleId) => {
    setRoleForm(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleId)
        ? prev.modules.filter(m => m !== moduleId)
        : [...prev.modules, moduleId]
    }));
  };

  if (loading) {
    return <div className="admin-loading">Cargando...</div>;
  }

  // Visibilidad según el rol del que está viendo el panel.
  const superAdminRoleIds = new Set(roles.filter(r => r.isSuperAdmin).map(r => r.id));
  const visibleUsers = viewerIsSuperAdmin
    ? users
    : users.filter(u => !superAdminRoleIds.has(u.roleId));
  const visibleRoles = viewerIsSuperAdmin
    ? roles
    : roles.filter(r => !r.isSuperAdmin);

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <h1>Panel de Administración</h1>
        <div className="admin-header-actions">
          <button onClick={() => navigate('/docs')} className="btn-secondary">
            Ver Documentación
          </button>
          <button onClick={onLogout} className="btn-logout">Cerrar Sesión</button>
        </div>
      </header>

      <div className="admin-content">
        <div className="admin-tabs">
          <button
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Usuarios ({visibleUsers.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'roles' ? 'active' : ''}`}
            onClick={() => setActiveTab('roles')}
          >
            Roles ({visibleRoles.length})
          </button>
        </div>

        {activeTab === 'users' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>Gestión de Usuarios</h2>
              <button onClick={() => openUserModal()} className="btn-primary">
                + Nuevo Usuario
              </button>
            </div>

            <div className="table-container">
              <table className="admin-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visibleUsers.map(user => {
                  const userRole = roles.find(r => r.id === user.roleId);
                  return (
                    <tr key={user.uid}>
                      <td>{user.displayName}</td>
                      <td>{user.email}</td>
                      <td>{userRole?.name || 'Sin rol'}</td>
                      <td>
                        <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                          {user.isActive ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td>
                        <button onClick={() => openUserModal(user)} className="btn-sm btn-edit">
                          Editar
                        </button>
                        <button
                          onClick={() => handleToggleUserStatus(user.uid, user.isActive)}
                          className={`btn-sm ${user.isActive ? 'btn-warn' : 'btn-success'}`}
                        >
                          {user.isActive ? 'Desactivar' : 'Activar'}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.uid)}
                          className="btn-sm btn-danger"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'roles' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>Gestión de Roles</h2>
              <button onClick={() => openRoleModal()} className="btn-primary">
                + Nuevo Rol
              </button>
            </div>

            <div className="table-container">
              <table className="admin-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Módulos Permitidos</th>
                  <th>Usuarios</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visibleRoles.map(role => {
                  const usersCount = users.filter(u => u.roleId === role.id).length;
                  return (
                    <tr key={role.id}>
                      <td>
                        {role.name}
                        {role.isSuperAdmin && <span className="badge-super"> SUPER ADMIN</span>}
                      </td>
                      <td>
                        {role.isSuperAdmin
                          ? 'Todos los módulos'
                          : `${role.permissions.modules.length} módulos`}
                      </td>
                      <td>{usersCount}</td>
                      <td>
                        {!role.isSuperAdmin && (
                          <>
                            <button onClick={() => openRoleModal(role)} className="btn-sm btn-edit">
                              Editar
                            </button>
                            <button
                              onClick={() => handleDeleteRole(role.id)}
                              className="btn-sm btn-danger"
                              disabled={usersCount > 0}
                            >
                              Eliminar
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Usuario */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => { setShowUserModal(false); resetUserForm(); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
            <div className="form-group">
              <label>Nombre</label>
              <input
                type="text"
                value={userForm.displayName}
                onChange={e => setUserForm({ ...userForm, displayName: e.target.value })}
                placeholder="Nombre completo"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={userForm.email}
                onChange={e => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="correo@ejemplo.com"
                disabled={!!editingUser}
              />
            </div>
            {!editingUser && (
              <div className="form-group">
                <label>Contraseña</label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="Contraseña"
                />
              </div>
            )}
            <div className="form-group">
              <label>Rol</label>
              <select
                value={userForm.roleId}
                onChange={e => setUserForm({ ...userForm, roleId: e.target.value })}
              >
                <option value="">Seleccionar rol</option>
                {visibleRoles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button onClick={() => { setShowUserModal(false); resetUserForm(); }} className="btn-secondary">
                Cancelar
              </button>
              <button
                onClick={editingUser ? handleUpdateUser : handleCreateUser}
                className="btn-primary"
              >
                {editingUser ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Rol */}
      {showRoleModal && (
        <div className="modal-overlay" onClick={() => { setShowRoleModal(false); resetRoleForm(); }}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <h2>{editingRole ? 'Editar Rol' : 'Nuevo Rol'}</h2>
            <div className="form-group">
              <label>Nombre del Rol</label>
              <input
                type="text"
                value={roleForm.name}
                onChange={e => setRoleForm({ ...roleForm, name: e.target.value })}
                placeholder="Ej: Editor, Visualizador, etc."
              />
            </div>
            <div className="form-group">
              <label>Módulos Permitidos</label>
              <div className="modules-list">
                {diagramCategories.map(category => (
                  <div key={category.id} className="category-section">
                    <h4 className="category-title">{category.title}</h4>
                    {category.subcategories && category.subcategories.map(subcat => (
                      <div key={subcat.id} className="subcategory-section">
                        <h5 className="subcategory-title">{subcat.title}</h5>
                        <div className="modules-grid">
                          {subcat.diagrams.map(diagram => (
                            <label key={diagram.id} className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={roleForm.modules.includes(diagram.id)}
                                onChange={() => toggleModule(diagram.id)}
                              />
                              <span>{diagram.id}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                    {category.diagrams && (
                      <div className="modules-grid">
                        {category.diagrams.map(diagram => (
                          <label key={diagram.id} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={roleForm.modules.includes(diagram.id)}
                              onChange={() => toggleModule(diagram.id)}
                            />
                            <span>{diagram.id}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => { setShowRoleModal(false); resetRoleForm(); }} className="btn-secondary">
                Cancelar
              </button>
              <button
                onClick={editingRole ? handleUpdateRole : handleCreateRole}
                className="btn-primary"
              >
                {editingRole ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
